import { escapeXmlEntities } from "@nova/util-strings"

export type Coord = { longitude: number; latitude: number; altitude?: number }

export type AltitudeMode = "clampToGround" | "relativeToGround" | "absolute"

export type KMLStyle = {
    line?: { color?: string; width?: number }
    poly?: { color?: string; fill?: string }
    icon?: { href?: string; scale?: number; color?: string; hotSpot?: { x: number; y: number } }
    label?: { scale?: number; color?: string }
}

export type PlacemarkOptions<T extends Record<string, unknown> = Record<string, unknown>> = {
    id?: string
    name?: string
    description?: string
    styleId?: string
    properties?: T
}

export type PointOptions<T extends Record<string, unknown> = Record<string, unknown>> =
    PlacemarkOptions<T> & { altitudeMode?: AltitudeMode }
export type LineOptions<T extends Record<string, unknown> = Record<string, unknown>> =
    PlacemarkOptions<T> & { altitudeMode?: AltitudeMode }
export type PolygonOptions<T extends Record<string, unknown> = Record<string, unknown>> =
    PlacemarkOptions<T> & {
        holes?: Coord[][]
        tessellate?: boolean
    }

const serializeExtendedData = (properties?: Record<string, unknown>) => {
    if (!properties) return ""
    const entries = Object.entries(properties)
    if (entries.length === 0) return ""
    return `<ExtendedData>${entries.map(([key, value]) => `<Data name="${escapeXmlEntities(key)}"><value>${escapeXmlEntities(String(value))}</value></Data>`).join("")}</ExtendedData>`
}

export class KMLBuilder {
    name: string
    description?: string

    private readonly styles: string[] = []
    private readonly placemarks: string[] = []
    private counts = { point: 0, line: 0, polygon: 0 }

    constructor(name: string, description?: string) {
        this.name = name
        this.description = description
    }

    addStyle(id: string, style: KMLStyle) {
        const { line, poly, icon, label } = style
        this.styles.push(`<Style id="${escapeXmlEntities(id)}">
    ${line ? `<LineStyle>${line.color ? `<color>${line.color}</color>` : ""}${line.width !== undefined ? `<width>${line.width}</width>` : ""}</LineStyle>` : ""}
    ${poly ? `<PolyStyle>${poly.color ? `<color>${poly.color}</color>` : ""}${poly.fill !== undefined ? `<color>${poly.fill}</color>` : "<fill>0</fill>"}</PolyStyle>` : ""}
    ${icon ? `<IconStyle>${icon.color ? `<color>${icon.color}</color>` : ""}${icon.scale !== undefined ? `<scale>${icon.scale}</scale>` : ""}${icon.href ? `<Icon><href>${escapeXmlEntities(icon.href)}</href></Icon>` : ""}${icon.hotSpot ? `<hotSpot x="${icon.hotSpot.x}" y="${icon.hotSpot.y}" xunits="pixels" yunits="pixels"/>` : ""}</IconStyle>` : ""}
    ${label ? `<LabelStyle>${label.color ? `<color>${label.color}</color>` : ""}${label.scale !== undefined ? `<scale>${label.scale}</scale>` : ""}</LabelStyle>` : ""}
</Style>`)
        return this
    }

    addPoint<T extends Record<string, unknown>>(coord: Coord, options?: PointOptions<T>) {
        const { longitude, latitude, altitude } = coord
        const n = ++this.counts.point
        const name = escapeXmlEntities(options?.name ?? `point-${n}`)
        const description = escapeXmlEntities(options?.description ?? `point-${n}`)
        this.placemarks
            .push(`<Placemark${options?.id ? ` id="${escapeXmlEntities(options.id)}"` : ""}>
    <name>${name}</name>
    <description>${description}</description>
    ${options?.styleId ? `<styleUrl>#${options.styleId}</styleUrl>` : ""}
    ${serializeExtendedData(options?.properties)}
    <Point>
        ${options?.altitudeMode ? `<altitudeMode>${options.altitudeMode}</altitudeMode>` : ""}
        <coordinates>${longitude},${latitude},${altitude ?? 0}</coordinates>
    </Point>
</Placemark>`)
        return this
    }

    addLine<T extends Record<string, unknown>>(coords: Coord[], options?: LineOptions<T>) {
        const n = ++this.counts.line
        const name = escapeXmlEntities(options?.name ?? `line-${n}`)
        const description = escapeXmlEntities(options?.description ?? `line-${n}`)
        this.placemarks
            .push(`<Placemark${options?.id ? ` id="${escapeXmlEntities(options.id)}"` : ""}>
    <name>${name}</name>
    <description>${description}</description>
    ${options?.styleId ? `<styleUrl>#${options.styleId}</styleUrl>` : ""}
    ${serializeExtendedData(options?.properties)}
    <LineString>
        ${options?.altitudeMode ? `<altitudeMode>${options.altitudeMode}</altitudeMode>` : ""}
        <coordinates>${coords.map(({ longitude, latitude, altitude }) => `${longitude},${latitude},${altitude ?? 0}`).join("\n")}</coordinates>
    </LineString>
</Placemark>`)
        return this
    }

    addPolygon<T extends Record<string, unknown>>(outer: Coord[], options?: PolygonOptions<T>) {
        const close = (ring: Coord[]) => {
            const first = ring[0]
            const last = ring[ring.length - 1]
            return first.longitude === last.longitude && first.latitude === last.latitude
                ? ring
                : [...ring, first]
        }
        const serializeRing = (ring: Coord[]) =>
            close(ring)
                .map(
                    ({ longitude, latitude, altitude }) =>
                        `${longitude},${latitude},${altitude ?? 0}`
                )
                .join("\n")

        const n = ++this.counts.polygon
        const name = escapeXmlEntities(options?.name ?? `polygon-${n}`)
        const description = escapeXmlEntities(options?.description ?? `polygon-${n}`)
        const holes = options?.holes ?? []
        this.placemarks
            .push(`<Placemark${options?.id ? ` id="${escapeXmlEntities(options.id)}"` : ""}>
    <name>${name}</name>
    <description>${description}</description>
    ${options?.styleId ? `<styleUrl>#${options.styleId}</styleUrl>` : ""}
    ${serializeExtendedData(options?.properties)}
    <Polygon>
        ${options?.tessellate ? "<tessellate>1</tessellate>" : ""}
        <outerBoundaryIs><LinearRing><coordinates>${serializeRing(outer)}</coordinates></LinearRing></outerBoundaryIs>
        ${holes.map((hole) => `<innerBoundaryIs><LinearRing><coordinates>${serializeRing(hole)}</coordinates></LinearRing></innerBoundaryIs>`).join("\n")}
    </Polygon>
</Placemark>`)
        return this
    }

    write() {
        return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2" xmlns:gx="http://www.google.com/kml/ext/2.2">
    <Document>
        <name>${escapeXmlEntities(this.name)}</name>
        ${this.description ? `<description>${escapeXmlEntities(this.description)}</description>` : ""}
        ${this.styles.join("\n")}
        ${this.placemarks.join("\n")}
    </Document>
</kml>`
    }
}
